import { Canvas, Point, TPointerEventInfo } from 'fabric';
import { useCallback, useEffect, useRef, useState } from 'react';

const ZOOM_MIN = 0.1;
const ZOOM_MAX = 10;
const ZOOM_STEP = 0.2;

const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(v, max));

/**
 * Handles canvas zoom relative to the fitted size.
 * A zoom value of 1 represents the fitted view at 100%.
 */
export const useCanvasZoom = (
  canvas: Canvas | null,
  baseZoom: number = 1,
  designWidth: number = 1,
  designHeight: number = 1
) => {
  const [zoom, setZoom] = useState(1);

  // Keeps the latest values available to event handlers without re-subscribing.
  const baseZoomRef = useRef(baseZoom);
  baseZoomRef.current = baseZoom;
  const sizeRef = useRef({ designWidth, designHeight });
  sizeRef.current = { designWidth, designHeight };

  // Applies zoom from the piece center to keep it anchored in place.
  const applyZoom = useCallback(
    (absoluteZoom: number) => {
      if (!canvas) return;
      const vpt = canvas.viewportTransform; // [scale, 0, 0, scale, offsetX, offsetY]
      const scale = vpt[0];
      const { designWidth: w, designHeight: h } = sizeRef.current;
      const pieceCenter = new Point(vpt[4] + (w * scale) / 2, vpt[5] + (h * scale) / 2);

      const z = clamp(absoluteZoom, ZOOM_MIN, ZOOM_MAX);
      canvas.zoomToPoint(pieceCenter, z);
      setZoom(z / baseZoomRef.current);
    },
    [canvas]
  );

  // Resets the displayed zoom when the canvas is fitted again.
  useEffect(() => {
    setZoom(1);
  }, [baseZoom]);

  // Handles wheel zoom using Fabric's current zoom level.
  useEffect(() => {
    if (!canvas) return;
    const handleWheel = (opt: TPointerEventInfo<WheelEvent>) => {
      applyZoom(canvas.getZoom() * 0.999 ** opt.e.deltaY);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    };
    canvas.on('mouse:wheel', handleWheel);
    return () => {
      canvas.off('mouse:wheel', handleWheel);
    };
  }, [canvas, applyZoom]);

  // Changes zoom in fixed steps relative to the fitted scale.
  const zoomIn = useCallback(() => {
    if (canvas) applyZoom(canvas.getZoom() + baseZoomRef.current * ZOOM_STEP);
  }, [canvas, applyZoom]);

  const zoomOut = useCallback(() => {
    if (canvas) applyZoom(canvas.getZoom() - baseZoomRef.current * ZOOM_STEP);
  }, [canvas, applyZoom]);

  // Syncs the displayed zoom after the canvas is fitted or centered.
  const resetZoom = useCallback(() => setZoom(1), []);

  return { zoom, zoomIn, zoomOut, resetZoom };
};
